import { useState, useMemo, useCallback, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { BookmarkIcon } from '@/components/ui/Icons';
import { api } from '@/api/client';
import type { MessengerMessage, MessengerThread } from '@/api/client';
import type { ChatContactUser } from '@/components/Messenger/sidebar/ChatSidebar';
import { getUserDisplayName } from '@/components/Messenger/sidebar/ChatSidebar';
import { matchesChatContactSearchQuery } from '@/components/Messenger/shared/chatContactSearch';
import { readMessengerPinnedContactIds } from '@/components/Messenger/shared/messengerPinnedContacts';
import { MessengerContactPickerPanel } from '@/components/Messenger/shared/MessengerContactPickerPanel';
import { MessengerPickerContactRow } from '@/components/Messenger/shared/MessengerPickerContactRow';

export interface ForwardMessageTargetPayload {
  threadId: string;
  peerId: string;
  peerName: string;
  threadKind?: 'direct' | 'group';
}

function forwardModalTitle(count: number): string {
  if (count === 1) return 'Переслать сообщение';
  const n10 = count % 10;
  const n100 = count % 100;
  if (n100 >= 11 && n100 <= 14) return `Переслать ${count} сообщений`;
  if (n10 === 1) return `Переслать ${count} сообщение`;
  if (n10 >= 2 && n10 <= 4) return `Переслать ${count} сообщения`;
  return `Переслать ${count} сообщений`;
}

interface ForwardMessageModalProps {
  open: boolean;
  message?: MessengerMessage | null;
  messages?: MessengerMessage[];
  users: ChatContactUser[];
  threads: MessengerThread[];
  savedThread: MessengerThread | null;
  savedThreadLoading?: boolean;
  currentUserId: string | null;
  onClose: () => void;
  onForward: (payload: ForwardMessageTargetPayload) => void;
  onThreadsRefresh?: () => void | Promise<void>;
}

export function ForwardMessageModal({
  open,
  message,
  messages: messagesProp,
  users,
  threads,
  savedThread,
  savedThreadLoading = false,
  currentUserId,
  onClose,
  onForward,
  onThreadsRefresh,
}: ForwardMessageModalProps) {
  const allMessages: MessengerMessage[] =
    messagesProp && messagesProp.length > 0 ? messagesProp : message ? [message] : [];

  const [query, setQuery] = useState('');
  const [creatingPeerId, setCreatingPeerId] = useState<string | null>(null);
  const [pinnedContactIds, setPinnedContactIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setPinnedContactIds(readMessengerPinnedContactIds());
  }, [open]);

  const q = query.trim().toLowerCase();

  const threadsByPeer = useMemo(() => {
    const m = new Map<string, MessengerThread>();
    for (const t of threads) {
      if (t.thread_kind === 'direct' && t.peer_id) m.set(t.peer_id, t);
    }
    return m;
  }, [threads]);

  const groupThreads = useMemo(
    () =>
      threads
        .filter((t) => t.thread_kind === 'group')
        .sort((a, b) => {
          const ta = a.last_message_at ?? '';
          const tb = b.last_message_at ?? '';
          return tb.localeCompare(ta);
        }),
    [threads]
  );

  const groupRowLabel = (t: MessengerThread) => (t.title?.trim() ? t.title.trim() : 'Группа');

  const filteredUsers = useMemo(
    () => (q.length >= 1 ? users.filter((u) => matchesChatContactSearchQuery(u, q)) : users),
    [users, q]
  );

  const visibleUsers = useMemo(
    () => filteredUsers.filter((u) => u.id !== currentUserId),
    [filteredUsers, currentUserId]
  );

  const usersByAccessAll = useMemo(() => {
    const groups = new Map<string | null, ChatContactUser[]>();
    for (const u of visibleUsers) {
      const key = u.access ?? null;
      const list = groups.get(key) ?? [];
      list.push(u);
      groups.set(key, list);
    }
    for (const [, list] of groups) {
      list.sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b), 'ru'));
    }
    return groups;
  }, [visibleUsers]);

  const accessKeysAll = useMemo(
    () =>
      Array.from(usersByAccessAll.keys()).sort((a, b) => {
        if (a == null) return 1;
        if (b == null) return -1;
        return a.localeCompare(b, 'ru');
      }),
    [usersByAccessAll]
  );

  /** Закреплённые сверху без отдельной секции, далее остальные по группам доступа */
  const forwardContactsOrdered = useMemo(() => {
    const byId = new Map(visibleUsers.map((u) => [u.id, u]));
    const seen = new Set<string>();
    const out: ChatContactUser[] = [];
    for (const id of pinnedContactIds) {
      const u = byId.get(id);
      if (u) {
        out.push(u);
        seen.add(u.id);
      }
    }
    for (const k of accessKeysAll) {
      for (const u of usersByAccessAll.get(k) ?? []) {
        if (!seen.has(u.id)) out.push(u);
      }
    }
    return out;
  }, [visibleUsers, pinnedContactIds, accessKeysAll, usersByAccessAll]);

  const savedLabel = 'Сохранённые сообщения';
  const savedRole = 'Только для вас';
  const savedRowMatchesSearch =
    q.length === 0 ||
    savedLabel.toLowerCase().includes(q) ||
    savedRole.toLowerCase().includes(q);
  const showSavedRow =
    savedRowMatchesSearch && (Boolean(savedThread) || savedThreadLoading);

  const handleClose = useCallback(() => {
    setQuery('');
    onClose();
  }, [onClose]);

  const handlePickUser = useCallback(
    async (user: ChatContactUser) => {
      const displayName = getUserDisplayName(user);
      const existing = threadsByPeer.get(user.id);
      if (existing?.id) {
        onForward({
          threadId: existing.id,
          peerId: user.id,
          peerName: existing.peer_name ?? displayName,
          threadKind: 'direct',
        });
        handleClose();
        return;
      }
      setCreatingPeerId(user.id);
      try {
        const thread = await api.messenger.createThreadWithPeer(user.id);
        await onThreadsRefresh?.();
        onForward({
          threadId: thread.id,
          peerId: thread.peer_id ?? user.id,
          peerName: thread.peer_name ?? displayName,
          threadKind: 'direct',
        });
        handleClose();
      } catch {
        /* остаёмся в модалке */
      } finally {
        setCreatingPeerId(null);
      }
    },
    [threadsByPeer, onForward, handleClose, onThreadsRefresh]
  );

  const handlePickSaved = useCallback(() => {
    if (!savedThread) return;
    onForward({
      threadId: savedThread.id,
      peerId: currentUserId ?? '',
      peerName: savedLabel,
      threadKind: 'direct',
    });
    handleClose();
  }, [savedThread, currentUserId, onForward, handleClose]);

  const handlePickGroup = useCallback(
    (t: MessengerThread) => {
      const label = t.title?.trim() ? t.title.trim() : 'Группа';
      onForward({
        threadId: t.id,
        peerId: '',
        peerName: label,
        threadKind: 'group',
      });
      handleClose();
    },
    [onForward, handleClose]
  );

  const filteredGroupThreads = useMemo(() => {
    if (q.length < 1) return groupThreads;
    return groupThreads.filter((gt) => {
      const label = gt.title?.trim() ? gt.title.trim() : 'Группа';
      return label.toLowerCase().includes(q);
    });
  }, [groupThreads, q]);

  if (!open || allMessages.length === 0) return null;

  const count = allMessages.length;
  const title = forwardModalTitle(count);
  const listBusy = creatingPeerId !== null;

  const showEmptySearch =
    q.length >= 1 &&
    !showSavedRow &&
    forwardContactsOrdered.length === 0 &&
    filteredGroupThreads.length === 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="md"
      headerVariant="island"
      containedOverlay
      className="messengerContactPicker"
    >
      <div
        className="messengerContactPicker__inner"
        data-picker-layout="forward"
      >
        <MessengerContactPickerPanel
          sectionLabel="Куда переслать"
          query={query}
          onQueryChange={setQuery}
          searchDisabled={listBusy}
          searchId="messengerPicker-forward-search"
        >
          {showSavedRow ? (
            <button
              type="button"
              className="messengerContactPicker__row messengerContactPicker__row_saved"
              disabled={listBusy || !savedThread}
              aria-busy={savedThreadLoading && !savedThread ? true : undefined}
              onClick={handlePickSaved}
            >
              <span className="messengerContactPicker__rowLead" aria-hidden>
                {savedThreadLoading && !savedThread ? (
                  <span className="chatSidebar__searchSpinner messengerContactPicker__savedRowSpinner" />
                ) : (
                  <BookmarkIcon size={20} className="chatSidebar__itemIcon chatSidebar__itemIcon_saved" />
                )}
              </span>
              <span className="chatSidebar__itemContent">
                <span className="chatSidebar__itemLabelRow">
                  <span className="chatSidebar__itemLabel messengerContactPicker__rowName">
                    {savedLabel}
                  </span>
                </span>
                <span className="chatSidebar__itemRole">
                  {savedThreadLoading && !savedThread ? 'Загрузка…' : savedRole}
                </span>
              </span>
            </button>
          ) : null}

          {filteredGroupThreads.length > 0 ? (
            <>
              <div className="messengerContactPicker__subsectionLabel">Группы</div>
              {filteredGroupThreads.map((gt) => (
                <button
                  key={gt.id}
                  type="button"
                  className="messengerContactPicker__row messengerContactPicker__row_forwardLayout"
                  disabled={listBusy}
                  onClick={() => handlePickGroup(gt)}
                >
                  <span className="chatSidebar__itemContent">
                    <span className="chatSidebar__itemLabelRow">
                      <span className="chatSidebar__itemLabel messengerContactPicker__rowName">
                        {groupRowLabel(gt)}
                      </span>
                    </span>
                  </span>
                </button>
              ))}
            </>
          ) : null}

          {forwardContactsOrdered.length > 0 ? (
            <>
              <div className="messengerContactPicker__subsectionLabel">Контакты</div>
              {forwardContactsOrdered.map((u) => (
                <MessengerPickerContactRow
                  key={u.id}
                  mode="single"
                  forwardLayout
                  user={u}
                  disabled={listBusy}
                  busy={creatingPeerId === u.id}
                  onPick={handlePickUser}
                />
              ))}
            </>
          ) : null}

          {showEmptySearch ? (
            <div className="messengerContactPicker__listStatus">Пользователи не найдены</div>
          ) : null}
        </MessengerContactPickerPanel>
      </div>
    </Modal>
  );
}
